import Ajv, { ErrorObject } from 'ajv';
import { JSONPath } from 'jsonpath-plus';
import { ProductSchema } from './types/IProductSchema';
import { productSchema as jsonSchema } from '../product-schema/schemas';
import { getReplacementValues } from '../utils';
import { ITenant } from '../../domains/inventory';
import { IProductViewModel } from './types/models';
import { Environment } from '../../config/env-config';

import { getProductJsonData, ProductCode } from './utils';

const ajv = new Ajv({ useDefaults: true });

class Product {
  public isValid = true;
  schemaErrors: ErrorObject[] | null | undefined = [];
  runtimeError: Error | undefined | null = null;
  private readonly resources: {
    tenant: ITenant;
    _self: ProductSchema;
  };

  public viewModel: IProductViewModel;
  private readonly _jsonData: ProductSchema;
  public isOnboarded = false;
  private env: Environment;

  constructor({ tenant, env }: { tenant: ITenant; env?: Environment }) {
    this.env = env;
    this.viewModel = {
      title: '',
      subTitle: '',
      description: '',
      icon: {
        iconUrl: '',
      },
      color: '', // should be a chakra color string, please see chakra docs
      link: '',
      productCode: '',
      id: '',
      organizationId: '',
    };

    const jsonData = this._getProductJsonData(tenant);

    this._jsonData = jsonData as ProductSchema;

    this.resources = {
      tenant,
      _self: jsonData as ProductSchema,
    };

    this._init();
  }

  private _init() {
    this._validateProductSchema();
    this._validateOnboarding();
    this.isValid && this._toViewModel();
  }

  private _validateProductSchema() {
    try {
      const validate = ajv.compile(jsonSchema);
      if (!this._jsonData)
        throw new Error('jsonSchema cannot be null or undefined');

      if (validate(this._jsonData)) {
        this.isValid = true;
      } else {
        this.schemaErrors = validate.errors;
        this.isValid = false;
      }
    } catch (err) {
      this.isValid = false;
      this.runtimeError = err as Error;
    }
  }

  private _toViewModel() {
    const { name } = this._jsonData || {};

    try {
      this.viewModel.title = name;
      this.viewModel.subTitle = this.getSubTitle();
      this.viewModel.description = this._jsonData.description;
      this.viewModel.icon.iconUrl = this._jsonData.icon.src;
      this.viewModel.color = this._jsonData.color;
      this.viewModel.link = this.getCtaLink();
      this.viewModel.productCode = this.resources.tenant.labels.ProductCode;
      this.viewModel.id = this.resources.tenant.id;
      this.viewModel.organizationId = this.resources.tenant.organizationId;
      this.viewModel.trialExpirationDate =
        this.resources.tenant.labels.TrialExpirationDate;
      this.viewModel.customerEnvironmentType =
        this.resources.tenant.labels.CustomerEnvironmentType;
    } catch (err) {
      this.isValid = false;
      this.runtimeError = err as Error;
    }
  }

  private _validateOnboarding() {
    const enabledProducts = (window as any).ENABLED_PRODUCTS ?? [];
    const notDisplayableProducts = new Set(
      (window as any).NOT_DISPLAYABLE_PRODUCTS ?? [],
    );

    const isOnboarded = enabledProducts
      .filter((x: string) => !notDisplayableProducts.has(x))
      .includes(this.resources.tenant?.labels.ProductCode);

    this.isOnboarded = isOnboarded;
  }

  private _getProductJsonData(tenant: ITenant) {
    try {
      const productCode = tenant.labels.ProductCode ?? '';
      const product = getProductJsonData(
        productCode.toLowerCase() as ProductCode,
      );
      if (!product) throw new Error('Unexpected product code');
      return product;
    } catch (err) {
      this.isValid = false;
      this.runtimeError = err as Error;
    }
  }

  private getSubTitle() {
    const {
      displayName: { source },
    } = this._jsonData || {};

    const replacements = this._jsonData.displayName?.source?.replacements;

    // Run replacement expressions if any
    if (replacements) {
      const { resultString: subTitle } = getReplacementValues({
        replacements,
        jsonPath: source.jsonPath,
        resources: this.resources,
        options: { env: this.env },
      });

      return subTitle;
    }

    // Else run json path expression
    let subTitle = JSONPath({
      json: this.resources[source.resource || '_self'],
      path: source.jsonPath,
    }).toString();

    // Run regex expressions if any
    if (source?.regex) {
      subTitle = subTitle.replace(
        new RegExp(source.regex?.match ?? ''),
        source.regex?.replaceValue ?? '',
      );
    }

    return subTitle;
  }

  public getCtaLink() {
    const link = this._jsonData.link.to;
    const replacements = this._jsonData.link?.replacements;

    if (!replacements) return link;

    const { resultString: finalUrl } = getReplacementValues({
      replacements,
      jsonPath: link,
      resources: this.resources,
      options: { env: this.env },
    });

    return finalUrl;
  }
}

export { Product };
